/**
 * AdmissionsSection - Sign up section with comparison cards and email form
 *
 * Layout:
 * - "SIGN UP" headline
 * - Two comparison cards (Traditional MBA vs MasterClass Executive)
 * - Email collection form that fires a Segment event on submission
 */
import Link from 'next/link'
import { useTranslation } from '@mc/i18n'
import { useStaticData } from '@mc/static-data-react'
import { EmailCaptureForm } from '../../../../components/EmailCaptureForm'
import { useFireEvent } from '../../../../tracking/educatorTracking'
import { paths } from '../../../../utils/paths'

/** Traditional MBA comparison items */
const TRADITIONAL_MBA_ITEMS = [
  { id: 'price', isBold: true },
  { id: 'duration', isBold: false },
  { id: 'commitment', isBold: false },
  { id: 'curriculum', isBold: false },
  { id: 'experience', isBold: false },
] as const

/** MasterClass Executive comparison items */
const EXECUTIVE_ITEMS = [
  { id: 'price', isBold: true },
  { id: 'duration', isBold: false },
  { id: 'commitment', isBold: false },
  { id: 'curriculum', isBold: false },
  { id: 'experience', isBold: false },
] as const

/** Items that have performance-variant copy overrides */
const PERFORMANCE_EXECUTIVE_ITEM_OVERRIDES = new Set(['duration', 'curriculum'])

/**
 * Comparison list item with top border
 */
interface ComparisonItemProps {
  text: string
  isBold: boolean
  isDark?: boolean
  superscript?: string
}

interface PricingStaticData {
  productPriceText?: string
  isProductPriceAvailable?: boolean
}

const ComparisonItem = ({
  text,
  isBold,
  isDark = false,
  superscript,
}: ComparisonItemProps) => (
  <div className='flex w-full flex-col gap-[10px] py-[5px]'>
    <div
      className='h-px w-full'
      style={{
        backgroundColor: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.2)',
      }}
    />
    <p
      className={`text-[14px] leading-[1.25] tracking-[-0.14px] ${
        isBold ? 'font-semibold' : ''
      } ${isDark ? 'text-white' : 'text-ja-black'}`}
    >
      {text}
      {superscript && (
        <sup className='text-[9px] leading-[1.25] italic'>{` ${superscript}`}</sup>
      )}
    </p>
  </div>
)

/**
 * Disclaimer footnotes below the Sign Up button
 */
const Disclaimer = () => {
  const { t } = useTranslation('@mc/educator')

  return (
    <div className='flex w-full flex-col items-start justify-center'>
      <div className='w-full whitespace-pre-wrap text-[0px] leading-[0] text-black'>
        <p className='mb-0'>
          <sup className='text-[6.45px] leading-[1.1]'>
            {t('enrollment.admissions.executive.disclaimer.footnote1Marker')}
          </sup>
          <span className='text-[10px] leading-[1.1]'>
            {t('enrollment.admissions.executive.disclaimer.footnote1Text')}
          </span>
        </p>
        <p>
          <sup className='text-[6.45px] leading-[1.1]'>
            {t('enrollment.admissions.executive.disclaimer.footnote2Marker')}
          </sup>
          <span className='text-[10px] leading-[1.1]'>
            {t('enrollment.admissions.executive.disclaimer.footnote2Text')}
          </span>
        </p>
      </div>
    </div>
  )
}

/**
 * Traditional MBA card (left card, dark background, no button)
 */
const TraditionalMBACard = ({ priceText }: { priceText: string }) => {
  const { t } = useTranslation('@mc/educator')

  return (
    <div className='flex w-full flex-col bg-ja-blue-dark px-5 py-10 md:w-[356px]'>
      <div className='flex flex-col gap-[30px]'>
        {/* Title */}
        <p className='text-[16px] font-semibold uppercase leading-[1.2] tracking-[0.32px] text-white'>
          {t('enrollment.admissions.traditionalMba.title')}
        </p>

        {/* Empty space for description alignment */}
        <div className='h-[40px]' />

        {/* Comparison list */}
        <div className='flex flex-col'>
          {TRADITIONAL_MBA_ITEMS.map((item) => (
            <ComparisonItem
              key={item.id}
              text={
                item.id === 'price'
                  ? priceText
                  : t(`enrollment.admissions.traditionalMba.items.${item.id}`)
              }
              isBold={item.isBold}
              isDark
            />
          ))}
          {/* Bottom border */}
          <div
            className='mt-[5px] h-px w-full'
            style={{ backgroundColor: 'rgba(255,255,255,0.3)' }}
          />
        </div>
      </div>
    </div>
  )
}

/**
 * MasterClass Executive card (right card, white background, with button)
 * Used on desktop only
 */
const ExecutiveCard = ({
  ctaText,
  ctaHref,
  priceText,
  isPerformance = false,
  isPrimaryLogIn = false,
}: {
  ctaText: string
  ctaHref: string
  priceText: string
  isPerformance?: boolean
  isPrimaryLogIn?: boolean
}) => {
  const { t } = useTranslation('@mc/educator')
  const fireEvent = useFireEvent()

  const getItemText = (itemId: string) => {
    if (itemId === 'price') return priceText
    if (isPerformance && PERFORMANCE_EXECUTIVE_ITEM_OVERRIDES.has(itemId)) {
      return t(`performance.admissions.executive.items.${itemId}`)
    }
    return t(`enrollment.admissions.executive.items.${itemId}`)
  }

  return (
    <div
      className='flex w-full flex-col gap-[20px] px-5 py-10 md:w-[356px]'
      style={{ backgroundColor: '#ffffff' }}
    >
      <div className='flex flex-col gap-[30px]'>
        {/* Title */}
        <p className='text-[16px] font-semibold uppercase leading-[1.2] tracking-[0.32px] text-ja-black'>
          {t('enrollment.admissions.executive.title')}
        </p>

        {/* Description with superscript after "MBA-style" */}
        <p className='text-[16px] leading-[1.25] tracking-[-0.16px] text-ja-black'>
          {t('enrollment.admissions.executive.descriptionBeforeSuper')}
          <sup className='text-[9px] leading-[1.25] italic'>
            {t('enrollment.admissions.executive.descriptionSuperscript')}
          </sup>
          {t('enrollment.admissions.executive.descriptionAfterSuper')}
        </p>

        {/* Comparison list */}
        <div className='flex flex-col'>
          {EXECUTIVE_ITEMS.map((item) => (
            <ComparisonItem
              key={item.id}
              text={getItemText(item.id)}
              isBold={item.isBold}
              superscript={
                item.id === 'price'
                  ? t('enrollment.admissions.executive.items.priceSuperscript')
                  : undefined
              }
            />
          ))}
          {/* Bottom border */}
          <div className='flex w-full flex-col gap-[10px] py-[5px]'>
            <div
              className='h-px w-full'
              style={{ backgroundColor: 'rgba(0,0,0,0.2)' }}
            />
          </div>
        </div>

        {/* Sign Up Button */}
        <Link
          href={ctaHref}
          onClick={() => {
            if (!isPrimaryLogIn) {
              fireEvent('educatorPurchaseClick', {
                cta_location: 'signup form',
                cta: ctaText,
              })
            }
            fireEvent('educatorSignClick', {
              action: ctaText.toLowerCase(),
              location: 'signup form',
            })
          }}
          className='flex w-full items-center justify-center bg-ja-blue-dark p-5 text-[16px] font-semibold text-white'
        >
          {ctaText}
        </Link>
      </div>

      {/* Disclaimer */}
      <Disclaimer />
    </div>
  )
}

/**
 * Mobile combined comparison card
 * Single white card with two columns: Traditional MBA (gray) vs Executive (black)
 */
const MobileCombinedCard = ({
  ctaText,
  ctaHref,
  executivePriceText,
  traditionalPriceText,
  isPerformance = false,
  isPrimaryLogIn = false,
}: {
  ctaText: string
  ctaHref: string
  executivePriceText: string
  traditionalPriceText: string
  isPerformance?: boolean
  isPrimaryLogIn?: boolean
}) => {
  const { t } = useTranslation('@mc/educator')
  const fireEvent = useFireEvent()

  const getExecutiveItemText = (itemId: string) => {
    if (itemId === 'price') return executivePriceText
    if (isPerformance && PERFORMANCE_EXECUTIVE_ITEM_OVERRIDES.has(itemId)) {
      return t(`performance.admissions.executive.items.${itemId}`)
    }
    return t(`enrollment.admissions.executive.items.${itemId}`)
  }

  return (
    <div
      className='flex w-full flex-col px-5 py-10'
      style={{ backgroundColor: '#ffffff' }}
    >
      <div className='flex flex-col gap-[30px]'>
        {/* Description with superscript after "MBA-style" */}
        <p className='text-[16px] leading-[1.25] tracking-[-0.16px] text-ja-black'>
          {t('enrollment.admissions.executive.descriptionBeforeSuper')}
          <sup className='text-[9px] leading-[1.25] italic'>
            {t('enrollment.admissions.executive.descriptionSuperscript')}
          </sup>
          {t('enrollment.admissions.executive.descriptionAfterSuper')}
        </p>

        {/* Two-column comparison */}
        <div className='flex gap-5'>
          {/* Traditional MBA column (grayed out) */}
          <div className='flex flex-1 flex-col gap-[10px]'>
            {/* Column title */}
            <p
              className='text-[14px] leading-[1.2] tracking-[0.28px] whitespace-pre-line'
              style={{ color: '#aaaaaa' }}
            >
              {t('enrollment.admissions.traditionalMba.titleMobile')}
            </p>

            {/* Items */}
            <div className='flex flex-col'>
              {TRADITIONAL_MBA_ITEMS.map((item) => (
                <div
                  key={item.id}
                  className='flex flex-col gap-[10px] py-[5px]'
                >
                  <div
                    className='h-px w-full'
                    style={{ backgroundColor: 'rgba(170,170,170,0.3)' }}
                  />
                  <p
                    className={`text-[14px] leading-[1.25] tracking-[-0.14px] ${item.isBold ? 'font-semibold' : ''}`}
                    style={{ color: '#aaaaaa' }}
                  >
                    {item.id === 'price'
                      ? traditionalPriceText
                      : t(
                          `enrollment.admissions.traditionalMba.items.${item.id}`,
                        )}
                  </p>
                </div>
              ))}
              {/* Bottom border */}
              <div
                className='mt-[5px] h-px w-full'
                style={{ backgroundColor: 'rgba(170,170,170,0.3)' }}
              />
            </div>
          </div>

          {/* MasterClass Executive column (black) */}
          <div className='flex flex-1 flex-col gap-[10px]'>
            {/* Column title */}
            <p className='text-[14px] font-semibold uppercase leading-[1.2] tracking-[0.28px] text-ja-black whitespace-pre-line'>
              {t('enrollment.admissions.executive.titleMobile')}
            </p>

            {/* Items */}
            <div className='flex flex-col'>
              {EXECUTIVE_ITEMS.map((item) => (
                <div
                  key={item.id}
                  className='flex flex-col gap-[10px] py-[5px]'
                >
                  <div
                    className='h-px w-full'
                    style={{ backgroundColor: 'rgba(0,0,0,0.2)' }}
                  />
                  <p
                    className={`text-[14px] leading-[1.25] tracking-[-0.14px] text-ja-black ${item.isBold ? 'font-semibold' : ''}`}
                  >
                    {getExecutiveItemText(item.id)}
                    {item.id === 'price' && (
                      <sup className='text-[9px] leading-[1.25] italic'>
                        {` ${t('enrollment.admissions.executive.items.priceSuperscript')}`}
                      </sup>
                    )}
                  </p>
                </div>
              ))}
              {/* Bottom border */}
              <div className='flex flex-col gap-[10px] py-[5px]'>
                <div
                  className='h-px w-full'
                  style={{ backgroundColor: 'rgba(0,0,0,0.2)' }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Sign Up Button */}
        <Link
          href={ctaHref}
          onClick={() => {
            if (!isPrimaryLogIn) {
              fireEvent('educatorPurchaseClick', {
                cta_location: 'signup form',
                cta: ctaText,
              })
            }
            fireEvent('educatorSignClick', {
              action: ctaText.toLowerCase(),
              location: 'signup form',
            })
          }}
          className='flex w-full items-center justify-center bg-ja-blue-dark p-5 text-[16px] font-semibold text-white'
        >
          {ctaText}
        </Link>

        {/* Disclaimer */}
        <Disclaimer />
      </div>
    </div>
  )
}

interface AdmissionsSectionProps {
  /**
   * 'enrollment' (default): standard headline only
   * 'performance': adds headline + subheadline above comparison cards,
   *   and uses variant copy for duration/curriculum executive items
   */
  variant?: 'enrollment' | 'performance'
  primaryCtaLabel?: string
  primaryCtaHref?: string
  isPrimaryLogIn?: boolean
  /**
   * Set to false when the caller renders its own heading above this band. The
   * caller then owns the space between its heading and the comparison cards.
   */
  showHeadline?: boolean
  /**
   * Set to false to let the band reach the viewport edge on mobile. Callers that
   * already sit inside their own page grid supply the side inset themselves.
   */
  withMobileInset?: boolean
}

export const AdmissionsSection = ({
  variant = 'enrollment',
  primaryCtaLabel,
  primaryCtaHref,
  isPrimaryLogIn = false,
  showHeadline = true,
  withMobileInset = true,
}: AdmissionsSectionProps) => {
  const { productPriceText, isProductPriceAvailable } =
    useStaticData<PricingStaticData>()
  const { t } = useTranslation('@mc/educator')
  const isPerformance = variant === 'performance'
  const ctaText = primaryCtaLabel ?? t('enrollment.admissions.signUpButton')
  const ctaHref = primaryCtaHref ?? paths.enrollmentApplication
  const executivePriceText =
    isProductPriceAvailable && productPriceText
      ? productPriceText
      : t('enrollment.admissions.executive.items.priceNoAmount')
  const traditionalPriceText = t(
    'enrollment.admissions.traditionalMba.items.price',
  )

  return (
    <section
      id='admissions'
      data-section='admissions'
      className={`relative z-[1] flex flex-col items-center bg-ja-blue md:p-0 ${
        withMobileInset ? 'p-[18.5px]' : ''
      }`}
    >
      {/* Headline — the performance variant adds a subheadline under it */}
      {showHeadline && (
        <div className='px-6 pb-[50px] pt-[80px] text-center md:pb-[80px] md:pt-[120px]'>
          <h2 className='font-serif text-[24px] uppercase leading-[1.1] tracking-[0.96px] text-white md:text-[28px] md:tracking-[1.12px]'>
            {isPerformance
              ? t('performance.admissions.headline')
              : t('enrollment.admissions.headline')}
          </h2>
          {isPerformance && (
            <p className='mx-auto mt-[20px] max-w-[500px] text-[16px] leading-[1.4] tracking-[-0.16px] text-white'>
              {t('performance.admissions.subheadline')}
            </p>
          )}
        </div>
      )}

      {/* Mobile Combined Card (hidden on desktop) */}
      <div className='w-full md:hidden'>
        <MobileCombinedCard
          ctaText={ctaText}
          ctaHref={ctaHref}
          executivePriceText={executivePriceText}
          traditionalPriceText={traditionalPriceText}
          isPerformance={isPerformance}
          isPrimaryLogIn={isPrimaryLogIn}
        />
      </div>

      {/* Desktop Comparison Cards (hidden on mobile) */}
      <div className='hidden w-full flex-col items-center gap-10 px-6 md:flex md:flex-row md:items-stretch md:justify-center md:px-0'>
        <TraditionalMBACard priceText={traditionalPriceText} />
        <ExecutiveCard
          ctaText={ctaText}
          ctaHref={ctaHref}
          priceText={executivePriceText}
          isPerformance={isPerformance}
          isPrimaryLogIn={isPrimaryLogIn}
        />
      </div>

      {/* Email Form */}
      <EmailCaptureForm />
    </section>
  )
}
